import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  AttachmentHierarchyItem,
  AttachmentService
} from '../../../services/attachment-service';
import { ClientDataFeedService, Client } from '../../../services/datafeed-service';
import { LoginService } from '../../../services/login-service';
import { DialogService } from '../../utility/dialog/dialog-service';
import { ModalType } from '../../utility/dialog-template/dialog-template.component';

interface HierarchyNode {
  id: string;
  parentHierarchyId: string | null;
  folderName: string;
  children: HierarchyNode[];
  expanded: boolean;
  depth: number;
  editing: boolean;
  editValue: string;
  addingChild: boolean;
  childDraft: string;
}

@Component({
  selector: 'app-attachment-hierarchy-editor',
  templateUrl: './attachment-hierarchy-editor.component.html',
  styleUrls: ['./attachment-hierarchy-editor.component.css'],
  standalone: false,
  encapsulation: ViewEncapsulation.None
})
export class AttachmentHierarchyEditorComponent implements OnInit {
  loading = false;
  saving = false;
  isGlobalAdmin = false;

  clients: Client[] = [];
  selectedClientId: string | null = null;

  flat: AttachmentHierarchyItem[] = [];
  tree: HierarchyNode[] = [];

  newRootName = '';

  constructor(
    private attachmentService: AttachmentService,
    private clientService: ClientDataFeedService,
    private loginService: LoginService,
    private snackBar: MatSnackBar,
    private dialogService: DialogService
  ) {}

  ngOnInit(): void {
    const user = this.loginService.userOptional;
    this.isGlobalAdmin = !!(user?.isGlobalAdmin || user?.isDeveloper);

    if (this.isGlobalAdmin) {
      this.clientService.getAllClients((clients) => {
        this.clients = (clients ?? []).filter(c => !!c.id);
        const preferred = user?.selectedClientId || user?.defaultClientId;
        if (preferred && this.clients.some(c => c.id === preferred)) {
          this.selectedClientId = preferred;
          this.loadHierarchies();
        }
      });
    } else {
      this.selectedClientId = user?.defaultClientId ?? null;
      if (this.selectedClientId)
        this.loadHierarchies();
    }
  }

  onClientChange(clientId: string | null): void {
    this.selectedClientId = clientId;
    this.flat = [];
    this.tree = [];
    if (clientId)
      this.loadHierarchies();
  }

  loadHierarchies(): void {
    if (!this.selectedClientId) return;
    this.loading = true;
    this.attachmentService.listHierarchies(this.selectedClientId).subscribe({
      next: (rows) => {
        this.flat = rows ?? [];
        this.tree = this.buildTree(this.flat);
        this.loading = false;
      },
      error: (err) => {
        this.dialogService.showError(this.errorMessage(err, 'Failed to load hierarchies'));
        this.loading = false;
      }
    });
  }

  private buildTree(items: AttachmentHierarchyItem[]): HierarchyNode[] {
    const byParent = new Map<string | null, AttachmentHierarchyItem[]>();
    for (const it of items) {
      const key = it.parentHierarchyId || null;
      if (!byParent.has(key)) byParent.set(key, []);
      byParent.get(key)!.push(it);
    }
    for (const list of byParent.values())
      list.sort((a, b) => a.folderName.localeCompare(b.folderName));

    const build = (parentId: string | null, depth: number): HierarchyNode[] => {
      const list = byParent.get(parentId) ?? [];
      return list.map(it => ({
        id: it.id,
        parentHierarchyId: it.parentHierarchyId || null,
        folderName: it.folderName,
        children: build(it.id, depth + 1),
        expanded: true,
        depth,
        editing: false,
        editValue: '',
        addingChild: false,
        childDraft: ''
      }));
    };

    return build(null, 0);
  }

  toggleExpand(node: HierarchyNode): void {
    node.expanded = !node.expanded;
  }

  flatten(): HierarchyNode[] {
    const out: HierarchyNode[] = [];
    const walk = (nodes: HierarchyNode[]) => {
      for (const n of nodes) {
        out.push(n);
        if (n.expanded) walk(n.children);
      }
    };
    walk(this.tree);
    return out;
  }

  addRoot(): void {
    const name = (this.newRootName ?? '').trim();
    if (!name || !this.selectedClientId) return;
    this.create(this.selectedClientId, name, null, () => {
      this.newRootName = '';
    });
  }

  startAddChild(node: HierarchyNode): void {
    node.expanded = true;
    node.addingChild = true;
    node.childDraft = '';
  }

  cancelAddChild(node: HierarchyNode): void {
    node.addingChild = false;
    node.childDraft = '';
  }

  commitAddChild(node: HierarchyNode): void {
    const name = (node.childDraft ?? '').trim();
    if (!name || !this.selectedClientId) return;
    this.create(this.selectedClientId, name, node.id, () => {
      node.addingChild = false;
      node.childDraft = '';
    });
  }

  startRename(node: HierarchyNode): void {
    node.editing = true;
    node.editValue = node.folderName;
  }

  cancelRename(node: HierarchyNode): void {
    node.editing = false;
    node.editValue = '';
  }

  commitRename(node: HierarchyNode): void {
    const name = (node.editValue ?? '').trim();
    if (!name || !this.selectedClientId) {
      this.cancelRename(node);
      return;
    }
    if (name === node.folderName) {
      this.cancelRename(node);
      return;
    }
    this.saving = true;
    this.attachmentService.renameHierarchy(this.selectedClientId, node.id, name).subscribe({
      next: () => {
        this.snackBar.open('Folder renamed', 'Close', { duration: 2000 });
        this.saving = false;
        this.loadHierarchies();
      },
      error: (err) => {
        this.dialogService.showError(this.errorMessage(err, 'Failed to rename folder'));
        this.saving = false;
      }
    });
  }

  delete(node: HierarchyNode): void {
    if (!this.selectedClientId) return;
    this.dialogService.openModal(
      'Delete Folder',
      `Delete folder "${node.folderName}"${node.children.length ? ' and all subfolders' : ''}? Folders that still contain attachments cannot be deleted.`,
      ModalType.QUESTION,
      () => {
        this.saving = true;
        this.attachmentService.deleteHierarchy(this.selectedClientId!, node.id).subscribe({
          next: () => {
            this.snackBar.open('Folder deleted', 'Close', { duration: 2000 });
            this.saving = false;
            this.loadHierarchies();
          },
          error: (err) => {
            this.dialogService.showError(this.errorMessage(err, 'Failed to delete folder'));
            this.saving = false;
          }
        });
      },
      () => { /* cancelled */ }
    );
  }

  private create(clientId: string, name: string, parentId: string | null, after: () => void): void {
    this.saving = true;
    this.attachmentService.createHierarchy(clientId, name, parentId).subscribe({
      next: () => {
        this.snackBar.open('Folder created', 'Close', { duration: 2000 });
        this.saving = false;
        after();
        this.loadHierarchies();
      },
      error: (err) => {
        this.dialogService.showError(this.errorMessage(err, 'Failed to create folder'));
        this.saving = false;
      }
    });
  }

  private errorMessage(err: any, fallback: string): string {
    return err?.error?.message
      ?? err?.error?.error
      ?? err?.error?.title
      ?? (typeof err?.error === 'string' ? err.error : null)
      ?? err?.message
      ?? fallback;
  }

  trackByNode = (_: number, n: HierarchyNode) => n.id;
}
